import React, { useState, useMemo, useRef, useEffect } from 'react';
import './SyringeVisualization.css';

export type SyringeSize = '0.3' | '0.5' | '1.0';

export interface SyringeVisualizationProps {
  injectionVolumeMl: number;
  syringeSize: SyringeSize;
  className?: string;
}

interface SyringeConfig {
  maxMl: number;
  totalUnits: number;
  majorUnitStep: number; // units between big numbers
  minorUnitStep: number; // units between small ticks
}

const SYRINGE_CONFIG: Record<SyringeSize, SyringeConfig> = {
  // 30-unit / 0.3 mL insulin syringe
  '0.3': {
    maxMl: 0.3,
    totalUnits: 30,
    majorUnitStep: 5,  // big tick every 5 units (0.05 mL)
    minorUnitStep: 1,  // small tick every 1 unit (0.01 mL)
  },
  // 50-unit / 0.5 mL insulin syringe
  '0.5': {
    maxMl: 0.5,
    totalUnits: 50,
    majorUnitStep: 10, // 0.1 mL
    minorUnitStep: 2,  // 0.02 mL
  },
  // 100-unit / 1.0 mL insulin syringe
  '1.0': {
    maxMl: 1.0,
    totalUnits: 100,
    majorUnitStep: 10, // 0.1 mL
    minorUnitStep: 2,  // 0.02 mL
  },
};

// Helper function to round to specified decimal places
const roundTo = (value: number, decimals: number): number => {
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
};

// Format volume for display
const formatMl = (vol: number): string => {
  if (vol >= 1) return vol.toFixed(1);
  if (vol >= 0.1) return vol.toFixed(2);
  return vol.toFixed(3);
};

// Format mL label (remove trailing zeros and decimal point if needed)
const formatMlLabel = (ml: number): string => {
  return ml.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
};

export const SyringeVisualization: React.FC<SyringeVisualizationProps> = ({
  injectionVolumeMl,
  syringeSize,
  className = '',
}) => {
  const cfg = SYRINGE_CONFIG[syringeSize];
  const barrelRef = useRef<HTMLDivElement>(null);
  const [barrelWidth, setBarrelWidth] = useState(0);
  const [labelWidths, setLabelWidths] = useState<Record<number, number>>({});
  
  // Derive mlPerUnit
  const mlPerUnit = cfg.maxMl / cfg.totalUnits;

  // Calculate clamped volume, fill percentage, and units
  const clampedMl = Math.min(Math.max(injectionVolumeMl || 0, 0), cfg.maxMl);
  const fillPercent = clampedMl / cfg.maxMl; // 0–1
  const units = clampedMl / mlPerUnit;
  const exceedsCapacity = injectionVolumeMl > cfg.maxMl;

  // Calculate plunger position in units and pixels
  const plungerUnits = clampedMl / mlPerUnit;
  const plungerX = barrelWidth > 0 ? (plungerUnits / cfg.totalUnits) * barrelWidth : 0;

  // Generate ticks in units using useMemo
  const ticks = useMemo(() => {
    const list: { units: number; isMajor: boolean }[] = [];
    for (let u = 0; u <= cfg.totalUnits; u += cfg.minorUnitStep) {
      const isMajor = u % cfg.majorUnitStep === 0 || u === 0 || u === cfg.totalUnits;
      list.push({ units: u, isMajor });
    }
    return list;
  }, [cfg]);

  // Handle edge case: show empty barrel when volume is 0 or invalid
  const hasValidVolume = injectionVolumeMl != null && !isNaN(injectionVolumeMl) && injectionVolumeMl > 0;

  // Update barrel width on resize
  useEffect(() => {
    const updateWidth = () => {
      if (barrelRef.current) {
        setBarrelWidth(barrelRef.current.offsetWidth);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const handleLabelLayout = (units: number, width: number) => {
    setLabelWidths((prev) => {
      if (prev[units] === width) {
        return prev;
      }
      return { ...prev, [units]: width };
    });
  };

  return (
    <div className={`syringe-visualization ${className}`}>
      {/* Syringe visualization */}
      <div className="syringe-visualization-container">
        {/* Barrel and needle container */}
        <div className="syringe-barrel-container">
          {/* Barrel container */}
          <div
            ref={barrelRef}
            className="syringe-barrel"
          >
            {/* Tick marks (lines inside barrel) */}
            {barrelWidth > 0 && (
              <div className="syringe-ticks">
                {ticks.map((tick) => {
                  const x = (tick.units / cfg.totalUnits) * barrelWidth;
                  
                  return (
                    <div
                      key={`tick-${tick.units}`}
                      className={`syringe-tick ${tick.isMajor ? 'syringe-tick-major' : 'syringe-tick-minor'}`}
                      style={{
                        left: `${x}px`,
                        transform: 'translateX(-50%)',
                      }}
                    />
                  );
                })}
              </div>
            )}

            {/* Fill region */}
            {hasValidVolume && (
              <div
                className="syringe-fill"
                style={{
                  width: `${fillPercent * 100}%`,
                }}
              />
            )}

            {/* Plunger indicator */}
            {barrelWidth > 0 && (
              <div
                className={`syringe-plunger ${hasValidVolume ? 'syringe-plunger-active' : ''}`}
                style={{
                  left: `${plungerX}px`,
                  transform: 'translateX(-50%)',
                }}
              />
            )}
          </div>

          {/* Needle/tip representation */}
          <div className="syringe-needle" />

          {/* Labels row below barrel */}
          {barrelWidth > 0 && (
            <div className="syringe-labels" style={{ width: `${barrelWidth}px` }}>
              {ticks
                .filter((tick) => tick.isMajor)
                .map((tick) => {
                  const x = (tick.units / cfg.totalUnits) * barrelWidth;
                  const ml = tick.units * mlPerUnit;
                  
                  return (
                    <div
                      key={`label-${tick.units}`}
                      className="syringe-label"
                      style={{
                        left: `${x}px`,
                        transform: `translateX(-${(labelWidths[tick.units] ?? 30) / 2}px)`,
                      }}
                      ref={(el) => {
                        if (el) {
                          const width = el.offsetWidth;
                          if (width !== labelWidths[tick.units]) {
                            handleLabelLayout(tick.units, width);
                          }
                        }
                      }}
                    >
                      <span className="syringe-label-units">
                        {tick.units}
                      </span>
                      <span className="syringe-label-ml">
                        {formatMlLabel(ml)}
                      </span>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>

      {/* Text label below */}
      {hasValidVolume ? (
        <p className="syringe-text">
          Draw {formatMl(clampedMl)} mL (≈ {roundTo(units, 1)} units on a {cfg.maxMl.toFixed(1)} mL syringe)
        </p>
      ) : (
        <p className="syringe-text-empty">
          Enter a dose to see how much to draw.
        </p>
      )}

      {/* Warning if exceeds capacity */}
      {exceedsCapacity && (
        <p className="syringe-warning">
          This dose exceeds the capacity of a {cfg.maxMl.toFixed(1)} mL syringe. Consider a larger size.
        </p>
      )}
    </div>
  );
};
